import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { formatCurrency } from '../../lib/utils';
import { 
  TrendingUp, 
  Calculator,
  CreditCard,
  Users,
  Receipt,
  FileText,
  User,
  Phone,
  Calendar
} from 'lucide-react';

const ProjectFinancialSummary = ({ project }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in-progress': return 'warning';
      case 'pending': return 'secondary';
      default: return 'secondary';
    }
  };

  const getProjectStatus = () => {
    if (!project) return 'pending';
    const paidAmount = project.totalPaidAmount || 0;
    const totalInstallmentAmount = project.totalPaymentAmount || 0;
    return paidAmount === totalInstallmentAmount ? 'completed' : 
           paidAmount > 0 ? 'in-progress' : 'pending';
  };

  const status = getProjectStatus();
  const statusText = {
    'completed': 'เสร็จสิ้น',
    'in-progress': 'กำลังดำเนินการ',
    'pending': 'รอดำเนินการ'
  };

  // Parse JSON fields safely (ลบตัวแปรที่ไม่ได้ใช้)

  const companyFee = (() => {
    try {
      return typeof project?.company_fee === 'string' ? JSON.parse(project.company_fee) : project?.company_fee;
    } catch (e) {
      return { type: 'percentage', percentage: 0, amount: 0 };
    }
  })();

  const paidAmount = project?.totalPaidAmount || 0;
  const totalPaymentAmount = project?.totalPaymentAmount || 0;
  const progressPercentage = totalPaymentAmount > 0 ? Math.round((paidAmount / totalPaymentAmount) * 100) : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ข้อมูลพื้นฐาน + สถิติด่วน */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <FileText className="h-5 w-5" />
            <span>ข้อมูลพื้นฐาน & สถิติด่วน</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* ข้อมูลพื้นฐาน */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">ข้อมูลโปรเจค</h3>
            <div className="grid grid-cols-1 gap-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">ชื่อโปรเจค:</span>
                </div>
                <p className="text-sm font-semibold">{project?.project_name || '-'}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">ลูกค้า:</span>
                </div>
                <p className="text-sm font-semibold">{project?.client_name || '-'}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">ชื่อ Sale:</span>
                </div>
                <p className="text-sm font-semibold">{project?.sales_name || '-'}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">เบอร์ติดต่อ Sale:</span>
                </div>
                <p className="text-sm font-semibold">{project?.sales_contact || '-'}</p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">วันที่เริ่มโปรเจค:</span>
                </div>
                <p className="text-sm font-semibold">
                  {project?.start_date ? new Date(project.start_date).toLocaleDateString('th-TH') : '-'}
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium text-muted-foreground">วันที่จบโปรเจค:</span>
                </div>
                <p className="text-sm font-semibold">
                  {project?.end_date ? new Date(project.end_date).toLocaleDateString('th-TH') : '-'}
                </p>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">สถานะ:</span>
                <Badge variant={getStatusColor(status)}>
                  {statusText[status]}
                </Badge>
              </div>
            </div>
          </div>

          {/* สถิติด่วน */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">สถิติด่วน</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg border border-blue-200 text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <CreditCard className="h-4 w-4 text-blue-600" />
                  <p className="text-xs font-medium text-blue-700">งวดชำระ</p>
                </div>
                <p className="text-sm font-bold text-blue-800">
                  {project?.paidInstallmentsCount || 0} / {project?.totalInstallmentsCount || 0}
                </p>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200 text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Users className="h-4 w-4 text-green-600" />
                  <p className="text-xs font-medium text-green-700">Developer</p>
                </div>
                <p className="text-sm font-bold text-green-800">
                  {project?.developer_payments?.length || 0} คน
                </p>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-orange-50 to-orange-100 rounded-lg border border-orange-200 text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Receipt className="h-4 w-4 text-orange-600" />
                  <p className="text-xs font-medium text-orange-700">ค่าใช้จ่ายโปรเจค</p>
                </div>
                <p className="text-sm font-bold text-orange-800">
                  {project?.other_expenses?.length || 0} รายการ
                </p>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-indigo-50 to-indigo-100 rounded-lg border border-indigo-200 text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <Calculator className="h-4 w-4 text-indigo-600" />
                  <p className="text-xs font-medium text-indigo-700">ค่าบริการบริษัท</p>
                </div>
                <p className="text-sm font-bold text-indigo-800">
                  {formatCurrency(companyFee.amount || 0)}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* สรุปการเงิน + การคำนวณสำคัญ */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingUp className="h-5 w-5" />
            <span>สถานะการชำระเงิน</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* สถานะการชำระเงิน */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-gray-700 border-b pb-2">สถานะการชำระเงิน</h3>
            <div className="space-y-3">
              {/* Payment Status Summary */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-gradient-to-r from-green-50 to-green-100 rounded-lg border border-green-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-green-700">ชำระแล้ว</span>
                    <span className="text-lg font-bold text-green-800">
                      {formatCurrency(paidAmount)}
                    </span>
                  </div>
                  <div className="text-xs text-green-600 mt-1">
                    {totalPaymentAmount > 0 ? Math.round((paidAmount / totalPaymentAmount) * 100) : 0}% ของยอดรวม
                  </div>
                </div>
                
                <div className="p-3 bg-gradient-to-r from-red-50 to-red-100 rounded-lg border border-red-200">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-red-700">ค้างชำระ</span>
                    <span className="text-lg font-bold text-red-800">
                      {formatCurrency(totalPaymentAmount - paidAmount)}
                    </span>
                  </div>
                  <div className="text-xs text-red-600 mt-1">
                    {totalPaymentAmount > 0 ? Math.round(((totalPaymentAmount - paidAmount) / totalPaymentAmount) * 100) : 0}% ของยอดรวม
                  </div>
                </div>
              </div>
              
              <div className="p-3 bg-gradient-to-r from-purple-50 to-purple-100 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-purple-700">ความคืบหน้าการชำระ</span>
                  <span className="text-lg font-bold text-purple-800">
                    {progressPercentage}%
                  </span>
                </div>
                <div className="mt-2 w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-gradient-to-r from-green-500 to-emerald-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progressPercentage}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectFinancialSummary;